/**
 * An envelope card's spending pie (TMMAPP-0108; audit 06 § A5, § Overrides 1; v2 L236–266): an
 * 84×84 pie (R 46 on a 100 viewBox, from twelve o'clock, slices stroked `surface`) of the envelope's
 * EXPENSE categories only — a category whose net is positive after refunds is left out — with a
 * legend of the top three and an "All categories" popover listing every slice with its share. The
 * popover opens on hover or focus and stays open on a click or Enter / Space until Escape, a second
 * press or leaving it; it is a portal, so the card never clips it. Funding is not a slice: the card
 * shows it as its own line. Slice colours are the categories' data colours (`categoricalFor`), the
 * pie is decorative (`aria-hidden`), and the legend and popover carry the names as text.
 */
import { useId, useState } from 'react';
import { MoneyParts } from '@/components/MoneyText';
import { Box, Popper, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatPercent } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { categoricalFor } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';
import type { PieSlice } from './spending';

const R = 46;
const C = 50;

function arc(from: number, sweep: number): string {
  if (sweep >= Math.PI * 2 - 0.001) {
    return `M ${C} ${C - R} A ${R} ${R} 0 1 1 ${C - 0.01} ${C - R} Z`;
  }
  const x1 = C + R * Math.cos(from);
  const y1 = C + R * Math.sin(from);
  const x2 = C + R * Math.cos(from + sweep);
  const y2 = C + R * Math.sin(from + sweep);
  return `M ${C} ${C} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 ${sweep > Math.PI ? 1 : 0} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
}

interface SpendingPieProps {
  slices: PieSlice[];
  currency: string | null;
}

export function SpendingPie({ slices, currency }: SpendingPieProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const popoverId = useId();
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  // `hover` follows the pointer and focus; `pinned` is a click or a key press, cleared by Escape.
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hover || pinned;
  const total = slices.reduce((sum, slice) => sum + slice.valueMinor, 0);
  const money = (minor: number) => <MoneyParts minor={minor} currency={currency} />;
  const share = (minor: number) => formatPercent(total === 0 ? 0 : minor / total, locale);

  const sweeps = slices.map((slice) =>
    total === 0 ? 0 : (slice.valueMinor / total) * Math.PI * 2,
  );
  const paths = slices.map((slice, index) => {
    const from = -Math.PI / 2 + sweeps.slice(0, index).reduce((sum, sweep) => sum + sweep, 0);
    return { ...slice, d: arc(from, sweeps[index] ?? 0) };
  });
  const swatch = (categoryId: number) => (
    <Box
      component="span"
      aria-hidden
      sx={{
        flex: '0 0 8px',
        width: 8,
        height: 8,
        borderRadius: '2px',
        bgcolor: categoricalFor(categoryId, tokens).fill,
      }}
    />
  );

  return (
    <>
      <Box
        component="button"
        type="button"
        ref={setAnchor}
        data-spending-pie=""
        aria-label={t('virtualAccount.card.breakdown')}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => {
          setHover(false);
          setPinned(false);
        }}
        onClick={() => setPinned((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && open) {
            event.stopPropagation();
            setHover(false);
            setPinned(false);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          width: '100%',
          p: '14px 18px 0',
          border: 0,
          background: 'none',
          color: tokens.text,
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'start',
          '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: -2 },
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 100 100"
          aria-hidden
          sx={{ flex: '0 0 84px', width: 84, height: 84, display: 'block' }}
        >
          {paths.map((slice) => (
            <path
              key={slice.categoryId}
              d={slice.d}
              fill={categoricalFor(slice.categoryId, tokens).fill}
              stroke={tokens.surface}
              strokeWidth={1}
            />
          ))}
        </Box>
        <Box
          component="span"
          sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}
        >
          {slices.slice(0, 3).map((slice) => (
            <Box
              component="span"
              key={slice.categoryId}
              data-legend-row=""
              sx={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}
            >
              {swatch(slice.categoryId)}
              <Box
                component="span"
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 12,
                  color: tokens.text2,
                }}
              >
                {slice.name}
              </Box>
              <Box
                component="span"
                sx={{ flex: '0 0 auto', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
              >
                {money(slice.valueMinor)}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Popper
        open={open}
        anchorEl={anchor}
        placement="top-end"
        sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
      >
        <Box
          id={popoverId}
          role="region"
          aria-label={t('envelope.allCategories')}
          data-category-popover=""
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          sx={{
            mb: '6px',
            minWidth: 246,
            maxWidth: 300,
            maxHeight: 240,
            overflow: 'auto',
            p: '8px 0',
            bgcolor: tokens.surfaceRaised,
            color: tokens.text,
            border: `1px solid ${tokens.line}`,
            borderRadius: `${tokens.radius.control}px`,
            boxShadow: tokens.elevation.dialog,
          }}
        >
          <Typography
            component="p"
            sx={{
              p: '2px 12px 7px',
              fontSize: 10,
              lineHeight: '14px',
              fontWeight: 500,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color: tokens.textLabel('surfaceRaised'),
            }}
          >
            {t('envelope.allCategories')}
          </Typography>
          {slices.map((slice) => (
            <Box
              key={slice.categoryId}
              data-popover-row=""
              sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '4px 12px' }}
            >
              {swatch(slice.categoryId)}
              <Box
                component="span"
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 12,
                }}
              >
                {slice.name}
              </Box>
              <Box
                component="span"
                sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: tokens.text2 }}
              >
                {money(slice.valueMinor)}
              </Box>
              <Box
                component="span"
                sx={{
                  flex: '0 0 34px',
                  textAlign: 'end',
                  fontSize: 12,
                  fontVariantNumeric: 'tabular-nums',
                  color: tokens.textLabel('surfaceRaised'),
                }}
              >
                {share(slice.valueMinor)}
              </Box>
            </Box>
          ))}
        </Box>
      </Popper>
    </>
  );
}
